import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { ContactSection } from "@/components/ContactSection";
import { CONTACT_EMAIL } from "@/lib/config";
import { IRONROOT_PRICING } from "@/lib/pricing";
import { trackStripeCta } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  CheckCircle, Robot, ListChecks, Broom, ArrowRight, Wallet, GraduationCap,
  ShareNetwork, Users, Sparkle, Star, Quotes, MagnifyingGlass, Repeat, ArrowsClockwise, Lightning,
} from "@phosphor-icons/react";

const PHASES = [
  { icon: GraduationCap, color: "#6E7F5B", title: "Bookkeeping Basics", desc: "Registration, documents, the T-accounts you need, and how to fix a trial balance that won't add up." },
  { icon: ListChecks, color: "#D96C4A", title: "Start the Books", desc: "Set up entity, EIN, bank accounts and the chart of accounts the right way from day one." },
  { icon: Wallet, color: "#4A6B53", title: "Track Money in Your Books", desc: "Record income and expenses, let AI categorize them, and see your Profit & Loss — all inside IronRoot." },
  { icon: Broom, color: "#8A6A3B", title: "Clean Up the Books", desc: "A systematic reconcile → categorize → verify → close process to rescue messy books." },
];

const FEATURES = [
  { icon: Robot, title: "Snap a receipt, AI categorizes it", desc: "Take a photo of any receipt — AI reads it, splits the line items, and suggests the right expense category. You just confirm." },
  { icon: MagnifyingGlass, title: "AI Diagnose My Books", desc: "Describe the mess (or paste a trial balance) and GPT-5.5 returns a prioritized, plain-English cleanup plan for your business." },
  { icon: Users, title: "See where your money goes", desc: "Confirmed receipts roll up into a clear spending-by-category view — your books, at a glance, no spreadsheet required." },
  { icon: ShareNetwork, title: "Ask AI anything, in plain English", desc: "\"How do I record a truck payment?\" \"Is this a write-off?\" Get context-aware answers on the exact step you're on." },
];

export default function Landing() {
  const { user } = useAuth();
  const ctaTo = user ? "/dashboard" : "/register";

  return (
    <div className="min-h-screen" style={{ background: "#F0E6D3" }}>
      {/* Founding member bar */}
      <div className="w-full text-center text-xs sm:text-sm font-semibold py-2 px-4" style={{ background: "var(--bk-gold)", color: "var(--bk-accent)" }} data-testid="founding-bar">
        <Sparkle size={14} weight="fill" className="inline mb-0.5 mr-1.5" style={{ color: "var(--bk-accent)" }} />
        Founding Members: <span style={{ color: "var(--bk-accent)" }}>40% off for life</span> + a 14-day free trial — from $29.40/month with code <span className="font-mono">FOUNDING40</span>
      </div>

      <Header />

      {/* Hero — warm western brand */}
      <section className="relative overflow-hidden py-20" style={{ background: "#F0E6D3" }}>
        <div className="relative max-w-7xl mx-auto px-6 flex flex-col md:flex-row gap-12 items-center">
          <div className="bk-fade-up w-full md:w-1/2 text-left">
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#1A2744", borderRadius: 50, padding: "6px 16px", color: "#F0E6D3", fontSize: 13, fontWeight: 600 }}>
              ✦ Financial Support From The Start
            </div>
            <h1 className="mt-5" style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 700, lineHeight: 1.15, color: "#1A2744" }}>
              Tired of the administrative nightmare?
            </h1>
            <p className="mt-4" style={{ fontSize: "clamp(1.1rem, 2vw, 1.4rem)", fontWeight: 700, fontStyle: "italic", color: "#8B4A2A", lineHeight: 1.4 }}>
              Financial Support From The Start — with your legal setup, books, and taxes all under one roof.
            </p>
            <p className="mt-4" style={{ fontSize: "1rem", color: "#2D2D2D", lineHeight: 1.7, maxWidth: 480 }}>
              My AI doesn't just keep your books — she tells you what to do next.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              {user ? (
                <Link to={ctaTo} data-testid="hero-cta" className="text-sm font-bold flex items-center gap-2 transition-all hover:-translate-y-0.5 rounded-full" style={{ background: "#1A2744", color: "#F0E6D3", padding: "14px 28px" }}>
                  {user ? "Go to dashboard" : "Start free"} <ArrowRight size={18} weight="bold" />
                </Link>
              ) : (
                <Link to="/register" onClick={() => trackStripeCta("Starter", "landing_hero", "Start free hero")} data-testid="hero-cta" className="text-sm font-bold flex items-center gap-2 transition-all hover:-translate-y-0.5 rounded-full" style={{ background: "#1A2744", color: "#F0E6D3", padding: "14px 28px" }}>
                  Start free <ArrowRight size={18} weight="bold" />
                </Link>
              )}
              <a href="#how-it-works" data-testid="hero-pricing" className="text-sm font-bold flex items-center gap-2 transition-all hover:-translate-y-0.5 rounded-full" style={{ background: "#F0E6D3", color: "#1A2744", border: "2px solid #1A2744", padding: "14px 28px" }}>
                See How It Works <ArrowRight size={16} weight="bold" />
              </a>
            </div>
          </div>

          <div className="bk-fade-up hidden sm:block w-full md:w-1/2" style={{ animationDelay: "0.15s" }}>
            <div className="flex items-center justify-center" style={{ borderRadius: 28, background: "rgba(255,255,255,0.52)", border: "1px solid rgba(26,39,68,0.12)", boxShadow: "0 20px 50px rgba(26,39,68,0.12)", aspectRatio: "4/3", width: "100%", padding: "2rem" }}>
              <div className="flex flex-col items-center justify-center gap-5 w-full h-full text-center">
                <img src="/ironroot-logo.png" alt="IronRoot" style={{ width: "100%", height: "78%", objectFit: "contain" }} />
                <div>
                  <div className="font-display" style={{ fontSize: "clamp(2rem, 3.2vw, 3rem)", fontWeight: 800, color: "#1A2744", letterSpacing: "-0.03em" }}>IronRoot</div>
                  <div style={{ color: "#8B4A2A", fontSize: "1rem", fontWeight: 600, marginTop: 6 }}>Financial Support From The Start</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--bk-accent)" }}>Pricing</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3" style={{ color: "var(--bk-text)" }}>
              Founding rates for the first 10 members.
            </h2>
            <p className="text-base mt-3 max-w-2xl" style={{ color: "var(--bk-text-2)" }}>
              Standard plans are $49, $99, and $199 per month. Founding members lock in $29.40, $59.40, or $119.40 per month for life.
            </p>
          </div>
          <Link to="/pricing" data-testid="landing-pricing-link" className="bk-btn-primary px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2">
            See every plan <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {IRONROOT_PRICING.map((plan, index) => (
            <div key={plan.tier} className="bk-card p-6 bk-fade-up" data-testid={`landing-pricing-card-${plan.tier}`} style={{ animationDelay: `${0.06 * index}s` }}>
              <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>{plan.name}</p>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-4xl font-bold" style={{ color: "var(--bk-text)" }}>${plan.foundingPrice.toFixed(2)}</span>
                <span className="text-sm mb-1" style={{ color: "var(--bk-text-2)" }}>/mo founding</span>
              </div>
              <p className="text-sm mt-2 line-through" style={{ color: "var(--bk-text-2)" }} data-testid={`landing-standard-price-${plan.tier}`}>Standard {plan.monthlyLabel}</p>
              <p className="text-sm mt-3" style={{ color: "var(--bk-success)" }} data-testid={`landing-founding-price-${plan.tier}`}>Founding {plan.foundingMonthlyLabel}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The wedge / pain */}
      <section className="border-y" style={{ borderColor: "var(--bk-border)", background: "rgba(255,255,255,0.72)" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: "var(--bk-text)" }}>
            "I started a business — nobody taught me the money part."
          </h2>
          <p className="text-base mt-4 max-w-2xl mx-auto" style={{ color: "var(--bk-text-2)" }}>
            Debits, receipts, categories, what's a write-off? It's overwhelming. IronRoot gives you a
            trusted path to follow and an AI coach that tells you exactly what to do next — so you can keep
            clean books without hiring anyone (yet).
          </p>
        </div>
      </section>

      {/* Workflow */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--bk-accent)" }}>The roadmap</span>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 mb-3" style={{ color: "var(--bk-text)" }}>
          Four roadmap steps. Every task spelled out.
        </h2>
        <p className="text-base mb-12 max-w-2xl" style={{ color: "var(--bk-text-2)" }}>
          Each roadmap step expands into detailed sub-steps and a pro tip — plus your own notes and sub-tasks per client.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PHASES.map((p, i) => (
            <div key={p.title} data-testid={`phase-highlight-${i}`} className="bk-card bk-lift p-7 bk-fade-up" style={{ animationDelay: `${0.08 * i}s` }}>
              <span className="w-12 h-12 rounded-md flex items-center justify-center mb-5" style={{ background: "var(--bk-page)" }}>
                <p.icon size={26} weight="duotone" color={p.color} />
              </span>
              <div className="font-mono text-xs mb-1" style={{ color: p.color }}>0{i + 1}</div>
              <h3 className="font-display text-xl font-bold mb-2" style={{ color: "var(--bk-text)" }}>{p.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--bk-text-2)" }}>{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="border-y" style={{ borderColor: "var(--bk-border)", background: "var(--bk-surface)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-x-12 gap-y-8 items-start">
          <div className="lg:sticky lg:top-24">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "var(--bk-text)" }}>
              More than a checklist.
            </h2>
            <p className="text-base mt-4" style={{ color: "var(--bk-text-2)" }}>
              The tools that turn a static guide into a real working file for every client you keep.
            </p>
            {user ? (
              <Link to={ctaTo} className="bk-btn-primary px-6 py-3 rounded-md text-sm font-semibold inline-flex items-center gap-2 mt-6">
                Open dashboard <ArrowRight size={18} weight="bold" />
              </Link>
            ) : (
              <Link to="/register" onClick={() => trackStripeCta("Professional", "landing_features", "Start free features")} className="bk-btn-primary px-6 py-3 rounded-md text-sm font-semibold inline-flex items-center gap-2 mt-6">
                Start free <ArrowRight size={18} weight="bold" />
              </Link>
            )}
          </div>
          <div className="space-y-4">
            {FEATURES.map((f, i) => (
            <div key={f.title} data-testid={`feature-${i}`} className="flex gap-4 p-5 rounded-2xl bk-fade-up bk-lift" style={{ background: "#fff", animationDelay: `${0.06 * i}s`, boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                <span className="w-11 h-11 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--bk-success-bg)" }}>
                  <f.icon size={24} weight="duotone" color="var(--bk-success)" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold mb-1" style={{ color: "var(--bk-text)" }}>{f.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--bk-text-2)" }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="max-w-4xl mx-auto px-6 lg:px-8 py-20 text-center">
        <div className="flex justify-center gap-1 mb-4">
          {[...Array(5)].map((_, i) => <Star key={i} size={20} weight="fill" color="#F0B429" />)}
        </div>
        <Quotes size={36} weight="fill" color="var(--bk-accent)" className="mx-auto mb-4" />
        <p className="font-display text-2xl sm:text-3xl font-medium leading-snug" style={{ color: "var(--bk-text)" }}>
          "This is exactly what I need — I'd use it to walk through the steps and find where my mistake is."
        </p>
        <p className="text-sm mt-5" style={{ color: "var(--bk-text-2)" }}>— An early user starting her own business</p>
      </section>

      {/* Launch spotlight — new capabilities */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-4 pb-8">
        <div className="rounded-[28px] p-8 sm:p-10 relative overflow-hidden" style={{ background: "var(--bk-gradient-primary)" }}>
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full blur-3xl opacity-30" style={{ background: "var(--bk-gradient-primary)" }} />
          <div className="relative">
            <span className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.14)", color: "#fff" }}>
              <Sparkle size={13} weight="fill" /> New for launch
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-4 text-white">
              Sell memberships &amp; put invoices on <span className="bk-gradient-text">autopilot</span>.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              {[
                { icon: Repeat, title: "Recurring memberships", desc: "Create a $99/mo offer and share a link — clients get billed on repeat automatically." },
                { icon: ArrowsClockwise, title: "Recurring invoices", desc: "Retainer clients get invoiced on schedule — drafts land ready for your review." },
                { icon: Lightning, title: "Active Assistant", desc: "Low-stock and empty-slot nudges — reorder or draft a promo post in one tap." },
              ].map((f) => (
                <div key={f.title} className="rounded-xl p-5" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <f.icon size={24} weight="duotone" style={{ color: "#FF8F00" }} />
                  <h3 className="font-display font-bold text-white mt-3">{f.title}</h3>
                  <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.72)" }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* A Note From the Founder */}
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-16" data-testid="founder-note">
        <div className="bk-card p-8 sm:p-12 relative overflow-hidden" style={{ background: "#FFFFFF" }}>
          <span className="text-xs uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--bk-accent)" }}>A note from the founder</span>
          <div className="mt-6 space-y-4 text-[15px] leading-relaxed" style={{ color: "var(--bk-text-2)" }}>
            <p>Before I built IronRoot, I ran my own consulting business.</p>
            <p>No matter how organized I tried to be, I found myself constantly switching between apps, searching for documents, answering the same questions, and trying to remember what needed to happen next — I even took a bookkeeping course trying to figure it out!</p>
            <p>I realized I wasn't spending my time <span className="font-semibold" style={{ color: "var(--bk-text)" }}>building</span> my business — I was spending it <span className="font-semibold" style={{ color: "var(--bk-text)" }}>managing</span> it.</p>
            <p>That's when I started asking myself a simple question: <span className="font-display italic" style={{ color: "var(--bk-text)" }}>What if one AI could handle all of this?</span></p>
            <p>Not just bookkeeping. An AI that helps you start your business, keeps your books organized, stores your important documents, reminds you of deadlines, answers your questions, and grows with your business every step of the way.</p>
            <p>That's why I built IronRoot. My goal has never been to create another bookkeeping app — I wanted to build the business partner I wish I'd had. One that's available 24/7 and helps entrepreneurs spend less time on administrative work and more time building the business they dreamed of, while saving them money.</p>
            <p>Thank you for trusting IronRoot to be part of your journey. I'm excited to help you build something incredible.</p>
            <p className="font-display text-lg font-semibold" style={{ color: "var(--bk-text)" }}>Welcome to IronRoot.</p>
          </div>
          <div className="flex items-center gap-4 mt-8 pt-6 border-t" style={{ borderColor: "var(--bk-border)" }}>
            <img src="/founder.jpg" alt="Kamery Bradshaw" className="w-16 h-16 rounded-full object-cover shrink-0" style={{ objectPosition: "center top" }} />
            <div>
              <p className="font-display text-2xl" style={{ color: "var(--bk-accent)", fontStyle: "italic" }}>Kamery Bradshaw</p>
              <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>Founder, IronRoot</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 pt-8">
        <div className="rounded-[28px] p-10 sm:p-16 text-center relative overflow-hidden" style={{ background: "#1A2744" }}>
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full blur-3xl opacity-20" style={{ background: "#8B4A2A" }} />
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter relative" style={{ color: "#F0E6D3" }}>
            Start free today and lock in <span style={{ color: "#E8B77E" }}>40% off for life</span>.
          </h2>
          <p className="text-base mt-4 max-w-xl mx-auto relative" style={{ color: "rgba(240,230,211,0.9)" }}>
            Run your books, sell memberships, and automate invoices in one place. Founding members who upgrade keep Starter at $29.40/mo, Professional at $59.40/mo, or Business at $119.40/mo with code <span className="font-mono font-bold" style={{ color: "#FFD34E" }}>FOUNDING40</span>.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-9 relative">
            {user ? (
              <Link to={ctaTo} data-testid="final-cta" className="px-8 py-3.5 rounded-full text-sm font-bold flex items-center gap-2" style={{ background: "#F0E6D3", color: "#1A2744" }}>
                Go to dashboard <ArrowRight size={18} weight="bold" />
              </Link>
            ) : (
              <Link to="/register" onClick={() => trackStripeCta("Starter", "landing_bottom", "Create free account bottom")} data-testid="final-cta" className="px-8 py-3.5 rounded-full text-sm font-bold flex items-center gap-2" style={{ background: "#F0E6D3", color: "#1A2744" }}>
                Start Free <ArrowRight size={18} weight="bold" />
              </Link>
            )}
            <a href="#how-it-works" className="px-7 py-3.5 rounded-full text-sm font-semibold border transition-colors" style={{ borderColor: "#F0E6D3", color: "#F0E6D3" }}>
              See How It Works
            </a>
          </div>
        </div>
      </section>

      <ContactSection />

      <footer className="border-t" style={{ borderColor: "var(--bk-border)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 flex flex-wrap items-center justify-between gap-3 text-sm" style={{ color: "var(--bk-text-2)" }}>
          <span className="flex items-center gap-2.5 font-display font-bold text-base" style={{ color: "var(--bk-text)" }}>
            <img src="/ironroot-logo.png" alt="IronRoot" className="w-[55px] h-[55px] object-contain" />
            IronRoot
          </span>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/about" data-testid="footer-about" className="hover:underline" style={{ color: "var(--bk-text-2)" }}>About</Link>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-email" className="hover:underline" style={{ color: "var(--bk-accent)" }}>{CONTACT_EMAIL}</a>
            <span>Bookkeeping, step by step. © {new Date().getFullYear()}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
